import { ArrowBack, DeleteOutline, EditOutlined, FileUpload, InfoOutlined, OpenInNew } from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  Fab,
  IconButton,
  Button as MuiButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useCallback, useContext, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import FieldRow from "../../components/mobile/FieldRow";
import { convertDate, convertToINR } from "../../helpers/Utils";
import {
  deleteInvoice,
  getInvoiceListByPO,
} from "../../services/invoiceService";
import { getPOByCode } from "../../services/poService";
import { latestInvoicePayment, paymentDetailsText as buildPaymentDetailsText } from "./utils/payments";
import OrderStatusBadge from "./components/OrderStatusBadge";
import GrnsSection from "./components/GrnsSection";
import LoaderV2 from "./components/LoaderV2";
import PoOverviewCards from "./components/PoOverviewCards";
import { MOBILE_FAB_SX } from "./components/mobileFabSx";
import AddressRows, { addressLines } from "./components/AddressRows";
import PoInvoiceCard from "./components/PoInvoiceCard";
import PoSummaryCard from "./components/PoSummaryCard";
import { ApprovalStatus, _STATUS } from "./constants/statusMapping";
import { getStatusLabel, getStatusMeta, EDITABLE_LIFECYCLE_STATUSES } from "./v2/constants/lifecycleStatus";
import { TOKENS } from "./constants/figmaTokens";
import { useToast } from "./hooks/useToast";
import { deriveCreditNoteBannerState } from "./utils/creditNote";
import { summariseInvoiceTableData } from "./utils/invoice";
import { derivePoGrnState } from "./utils/poGrnAggregation";
import { usePermissions } from "../../context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "../../constants/bouncerPermissions";
import { WithPermission } from "./components/WithPermission";
import { getBackendErrorMessage } from "../../helpers/errorUtils";
import CreditNoteReview from "./CreditNoteReview";
import UploadInvoicesModal from "./v2/modals/UploadInvoicesModal";
import { useInvoiceUploadFlow } from "./v2/hooks/useInvoiceUploadFlow";
import { useBatchManager } from "./v2/hooks/useBatchManager";
import { isUploadBlocked } from "./v2/utils/invoiceUpload";


/**
 * Maps a StatusMeta variant to the legacy _STATUS code used by the chip colour system.
 * "info"/default variants intentionally return undefined — the chip falls back to its default style.
 */
function variantToStatusCode(variant) {
  if (variant === "success") return _STATUS.success;
  if (variant === "error")   return _STATUS.error;
  if (variant === "warning") return _STATUS.warn;
  return undefined;
}

// Column header style shared with PO items table
const ITEM_COL_HEADER_SX = {
  fontSize: 12,
  fontWeight: 500,
  textTransform: "uppercase",
  color: TOKENS.onSurfaceSecondary,
};

function SectionHeading({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 16,
        fontWeight: 700,
        lineHeight: "24px",
        color: TOKENS.onSurfaceDefault,
        marginBottom: 2,
      }}
    >
      {children}
    </Typography>
  );
}

function AddressBlock({ heading, bodyAddress, pin, gst }) {
  if (!bodyAddress) {
    return (
      <Box>
        <SectionHeading>{heading}</SectionHeading>
        <Typography sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceSecondary }}>
          -
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <SectionHeading>{heading}</SectionHeading>
      {addressLines(bodyAddress, pin, gst).map((line, idx) => (
        <Typography key={idx} sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceDefault }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

const UploadView = ({ data, onUploadClick, canUpload = true }) => {
  const { image } = data;
  if (!image || !image.pre_signed_url)
    return canUpload ? (
      <MuiButton
        variant="text"
        size="small"
        startIcon={<FileUpload fontSize="small" />}
        onClick={onUploadClick}
        sx={{ textTransform: "none", fontSize: "12px" }}
      >
        Upload invoice
      </MuiButton>
    ) : null;
  return (
    <Box padding="6px">
      <a href={image.pre_signed_url} target="_blank" rel="noreferrer">
        {image.file_name || "Invoice Link"}
      </a>
    </Box>
  );
};

const InfoTooltip = ({ title }) => {
  if (!title) return null;
  return (
    <Tooltip title={title}>
      <InfoOutlined fontSize="small" />
    </Tooltip>
  );
};

const InvoiceDetailsV2 = () => {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isInternal } = useContext(UserContext);
  const { showToast } = useToast();
  const isMobile = useIsMobile();

  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedUploadInvoiceId, setSelectedUploadInvoiceId] = useState(undefined);

  const { po_code } = params || {};

  const showErrorToast = ({ message, error }) =>
    showToast({ type: "error", message: message + getBackendErrorMessage(error) });

  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(BOUNCER_PERMISSIONS.INVOICE_EDIT);
  const canDelete = hasPermission(BOUNCER_PERMISSIONS.INVOICE_DELETE);

  const batchManager = useBatchManager({
    onBatchComplete: useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
    }, [queryClient]),
  });

  const flow = useInvoiceUploadFlow({
    onBatchStarted: batchManager.addBatch,
    poCode: po_code,
    invoiceId: selectedUploadInvoiceId,
  });

  // Invoice list (v1 API — returns all invoices for the PO, both v1 and v2)
  const { isLoading, isError, data, error, isFetching } = useQuery(
    ["invoiceList", po_code, "grns+three_way_match"],
    () => getInvoiceListByPO({ po_code, include: "grns,three_way_match" }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    }
  );

  // FINS PO rich data — gracefully absent for IMS POs (query error is swallowed)
  const { data: po } = useQuery(
    ["po-by-code", po_code],
    () => getPOByCode(po_code),
    { retry: false, refetchOnWindowFocus: false, enabled: !!po_code }
  );

  const { isLoading: isDeleting, mutate: deleteInvoiceMutate } = useMutation(
    deleteInvoice,
    {
      onSuccess: (res) => {
        showToast({
          type: "success",
          message: `Invoice ${res.invoice_no || ""} deleted successfully`,
        });
        queryClient.invalidateQueries({ queryKey: ["invoiceList"] });
      },
      onError: (error) => {
        showErrorToast({ error, message: "Error Deleting invoice " });
      },
    }
  );

  const handleDelete = (invoiceNo) => {
    deleteInvoiceMutate({ invoiceNo, empId: user?.email, po_code });
  };

  const handleUploadClose = () => {
    setUploadOpen(false);
    setSelectedUploadInvoiceId(undefined);
    flow.reset();
  };

  if (!po_code) return <div>No PO Code Found</div>;
  if (isLoading || isFetching || isDeleting) return <LoaderV2 />;
  if (isError) return <div>{error.message}</div>;

  const poData = data?.po_list?.[0];
  const invoices = poData?.invoices ?? [];
  const { totalInvoicedValue, approvedCount, approvedInvoiceAmount } =
    summariseInvoiceTableData(invoices);
  // Payments come from the PO details response (ENG-27224): `payments[]` is the
  // union of legacy CSV rows and v2 UTR lines, `total_amount_paid` their sum.
  const payments = poData?.payments ?? [];
  const totalPaid = poData?.total_amount_paid ?? 0;
  const items = po?.po_items ?? [];

  const isPaymentDateExist = (invoice) => latestInvoicePayment(payments, invoice.invoice_no);

  const cnBanner = deriveCreditNoteBannerState(invoices);

  const poAmount = poData?.order_value ?? 0;
  const { grnRows, totalGrnValue, threeWayMatchState } = derivePoGrnState(
    invoices,
    poData?.received_grns ?? []
  );

  // lifecycle_status present → v2 invoice: use role/source-aware mapping.
  // Absent → v1 invoice: fall back to invoice_status.label.
  const invoiceStatusDisplay = (invoice) => ({
    statusLabel: invoice.lifecycle_status
      ? getStatusLabel(invoice.lifecycle_status, isInternal, poData?.source)
      : (invoice.invoice_status?.label ?? "-"),
    statusCode: invoice.lifecycle_status
      ? variantToStatusCode(getStatusMeta(invoice.lifecycle_status).variant)
      : (invoice.invoice_status?.state
          ? ApprovalStatus[invoice.invoice_status.state]?.code
          : undefined),
  });

  const rejectReasonFor = (invoice) =>
    invoice.invoice_status?.state === "rejected"
      ? (invoice.system_rejection_reason ||
          invoice.vm_reject_reason ||
          invoice.l1_reject_reason ||
          invoice.l2_reject_reason ||
          "Invoice value is greater than PO value")
      : undefined;

  const handleDeleteClick = (invoice) => {
    if (invoice.invoice_status?.label === "completed") {
      showToast({ type: "error", message: "Cannot be edited/deleted after completion" });
      return;
    }
    if (invoice.l1_approval_status === "approved" && invoice.l2_approval_status !== "rejected") {
      showToast({ type: "error", message: "Permission denied" });
      return;
    }
    handleDelete(invoice.invoice_no);
  };

  const invoiceActions = (invoice) => {
    const actions = [];
    const image = invoice.invoice_image;
    if (image?.pre_signed_url) {
      actions.push({
        label: "View invoice copy",
        icon: <OpenInNew fontSize="small" />,
        onClick: () => window.open(image.pre_signed_url, "_blank", "noreferrer"),
      });
    } else if (!isUploadBlocked(invoice)) {
      actions.push({
        label: "Upload invoice copy",
        icon: <FileUpload fontSize="small" />,
        onClick: () => {
          setSelectedUploadInvoiceId(invoice.id);
          setUploadOpen(true);
        },
      });
    }
    if (canEdit && EDITABLE_LIFECYCLE_STATUSES.has(invoice.lifecycle_status)) {
      actions.push({
        label: "Edit",
        icon: <EditOutlined fontSize="small" />,
        onClick: () => navigate(`/dashboard/invoice-management/v2/${invoice.id}/edit`),
      });
    }
    if (canDelete) {
      actions.push({
        label: "Delete",
        icon: <DeleteOutline fontSize="small" />,
        destructive: true,
        onClick: () => handleDeleteClick(invoice),
      });
    }
    return actions;
  };

  const approvedInvoiceText =
    approvedCount > 0
      ? `${convertToINR(approvedInvoiceAmount)} (${approvedCount} Invoice${approvedCount > 1 ? "s" : ""})`
      : "No invoice";
  const paymentDetailsText = buildPaymentDetailsText(totalPaid, payments.length);
  const supplierAddress = {
    bodyAddress: po?.vendor_details?.vendor_billing_address,
    pin: po?.vendor_details?.vendor_billing_address?.post_code,
    gst: po?.vendor_details?.gst_tin,
  };
  const deliveryAddress = {
    bodyAddress: po?.shipping_address,
    pin: po?.billing_address?.post_code,
    gst: po?.billing_address?.tax_id,
  };

  // Reusable sub-header
  const subHeader = (
    <Box
      sx={{
        bgcolor: TOKENS.surfaceDefault,
        borderBottom: `1px solid ${TOKENS.surfaceVariant}`,
        display: "flex",
        alignItems: "center",
        height: isMobile ? "auto" : 60,
        py: isMobile ? "4px" : 0,
        pl: "8px",
        pr: isMobile ? "12px" : "24px",
        flexShrink: 0,
      }}
    >
      <Box
        data-testid="back-button"
        onClick={() => navigate(-1)}
        sx={{
          width: 36,
          height: 36,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "4px",
          cursor: "pointer",
          "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
        }}
      >
        <ArrowBack sx={{ fontSize: 24, color: TOKENS.onSurfaceSecondary }} />
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-start" pb="4px">
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}>
          Orders/
        </Typography>
        <Typography
          sx={{ fontSize: 20, fontWeight: 600, lineHeight: "24px", color: TOKENS.onSurfaceDefault }}
        >
          {po_code}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {!isMobile && (
        <Box display="flex" gap="24px" alignItems="center">
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Typography sx={{ fontSize: 10, lineHeight: "12px", color: TOKENS.onSurfaceSecondary }}>
              Order Value
            </Typography>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px", color: TOKENS.onSurfaceDefault }}
            >
              {convertToINR(poData?.order_value)}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Typography sx={{ fontSize: 10, lineHeight: "12px", color: TOKENS.onSurfaceSecondary }}>
              Invoice Value
            </Typography>
            <Typography
              sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px", color: TOKENS.onSurfaceDefault }}
            >
              {convertToINR(totalInvoicedValue)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  const uploadModal = (
    <UploadInvoicesModal
      open={uploadOpen}
      onClose={handleUploadClose}
      phase={flow.phase}
      items={flow.items}
      summary={flow.summary}
      rejectedFiles={flow.rejectedFiles}
      batchStartError={flow.batchStartError}
      maxFiles={flow.maxFiles}
      activeBatches={batchManager.batches}
      onDismissBatch={batchManager.dismissBatch}
      onFilesSelected={flow.addFiles}
      onUploadAll={flow.startUpload}
      onRemoveFile={flow.cancelFile}
      onRetryFile={flow.retryFile}
      onRetryAllFailed={flow.retryAllFailed}
      onRetryBatchProcessing={flow.retryBatchProcessing}
      onDismissRejected={flow.dismissRejected}
    />
  );

  return (
    <Box sx={{ bgcolor: TOKENS.surfaceBg, minHeight: "100vh" }}>
      {subHeader}

      <Box sx={{ padding: isMobile ? "12px 12px 88px" : "24px", display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "24px" }}>
        {/* Credit note banners */}
        {cnBanner.mode === "pending" && (
          <Alert severity="warning">Awaiting approval</Alert>
        )}
        {cnBanner.mode === "rejected" && (
          <Alert severity="error">
            Credit note{cnBanner.numbers.length > 1 ? "s" : ""}{" "}
            {cnBanner.numbers.join(", ")} rejected. {cnBanner.reasonText}. Please re-upload.
          </Alert>
        )}

        {isMobile && poData && <PoSummaryCard po={poData} invoicedValue={totalInvoicedValue} />}

        {isInternal && (
          <PoOverviewCards
            poAmount={poAmount}
            invoicesRaisedAmount={totalInvoicedValue}
            invoiceCount={invoices.length}
            approvedAmount={approvedInvoiceAmount}
            approvedCount={approvedCount}
            grnValue={totalGrnValue}
            threeWayMatchState={threeWayMatchState}
            paidAmount={totalPaid}
            showGrnCard={!po}
          />
        )}

        {/* Approved invoice + Payment summary */}
        <Card sx={{ bgcolor: TOKENS.surfaceDefault, borderRadius: "8px", p: { xs: "16px", md: "24px" }, boxShadow: "none" }}>
          {isMobile ? (
            <Stack gap={1}>
              <FieldRow label="Approved invoice" value={approvedInvoiceText} />
              <FieldRow label="Payment details" value={paymentDetailsText} />
            </Stack>
          ) : (
          <Box display="flex" gap="48px">
            <Box flex={1}>
              <SectionHeading>Approved invoice</SectionHeading>
              <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
                {approvedInvoiceText}
              </Typography>
            </Box>
            <Box flex={1}>
              <SectionHeading>Payment details</SectionHeading>
              <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
                {paymentDetailsText}
              </Typography>
            </Box>
          </Box>
          )}
        </Card>

        {/* Supplier + Delivery addresses — FINS POs only */}
        {po && (
          <Card sx={{ bgcolor: TOKENS.surfaceDefault, borderRadius: "8px", p: { xs: "16px", md: "24px" }, boxShadow: "none" }}>
            {isMobile ? (
              <Stack gap={1}>
                <FieldRow label="Supplier Address" value={<AddressRows {...supplierAddress} />} />
                <FieldRow label="Delivery Address" value={<AddressRows {...deliveryAddress} />} />
              </Stack>
            ) : (
            <Box display="flex" gap="48px">
              <Box flex={1}>
                <AddressBlock heading="Supplier Address" {...supplierAddress} />
              </Box>
              <Box flex={1}>
                <AddressBlock heading="Delivery Address" {...deliveryAddress} />
              </Box>
            </Box>
            )}
          </Card>
        )}

        {/* Invoice Details table */}
        <Card
          sx={{
            bgcolor: TOKENS.surfaceDefault,
            borderRadius: "8px",
            boxShadow: "none",
            p: { xs: "16px", md: "24px" },
          }}
        >
          {/* Table header row */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px" }}>
                Invoice Details
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: "20px", color: TOKENS.onSurfaceSecondary }}>
                Total Invoiced Value:{" "}
                <Box component="span" sx={{ color: TOKENS.onSurfaceDefault, fontWeight: 500 }}>
                  {convertToINR(totalInvoicedValue)} of{" "}
                  {convertToINR(poData?.order_value || 0)}
                </Box>
              </Typography>
            </Box>
            {!isMobile && (
              <WithPermission permission={BOUNCER_PERMISSIONS.INVOICE_UPLOAD}>
                <MuiButton
                  variant="outlined"
                  size="small"
                  startIcon={<FileUpload fontSize="small" />}
                  onClick={() => {
                    setSelectedUploadInvoiceId(undefined);
                    setUploadOpen(true);
                  }}
                  sx={{ textTransform: "none", flexShrink: 0 }}
                >
                  Upload Invoices
                </MuiButton>
              </WithPermission>
            )}
          </Box>

          {invoices.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              py={6}
              gap={2}
            >
              <img
                src={require("./assets/done.png")}
                height={80}
                width={80}
                alt="no invoices"
              />
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: TOKENS.onSurfaceDefault }}>
                No invoices for this PO
              </Typography>
            </Box>
          ) : isMobile ? (
            <Stack gap={1}>
              {invoices.map((invoice) => {
                const { statusLabel, statusCode } = invoiceStatusDisplay(invoice);
                return (
                  <PoInvoiceCard
                    key={invoice.id}
                    invoice={invoice}
                    statusLabel={statusLabel}
                    statusCode={statusCode}
                    paymentDate={isPaymentDateExist(invoice)?.payment_date}
                    rejectReason={rejectReasonFor(invoice)}
                    actions={invoiceActions(invoice)}
                    onOpen={() => navigate(`/dashboard/invoice-management/v2/${invoice.id}`)}
                    onCreditNote={setSelectedCreditNote}
                  />
                );
              })}
            </Stack>
          ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Number</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Vendor Name</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Date</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Value</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Amount Paid</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Addl. Charges</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>IRN</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Credit Note</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Invoice Copy</TableCell>
                  <TableCell sx={ITEM_COL_HEADER_SX}>Status</TableCell>
                  {(canEdit || canDelete) && (
                    <TableCell sx={ITEM_COL_HEADER_SX}>Actions</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice) => {
                  const paymentEntry = isPaymentDateExist(invoice);
                  const { statusLabel, statusCode } = invoiceStatusDisplay(invoice);

                  return (
                    <TableRow key={invoice.id}>
                      {/* Invoice number — links to v2 detail page for all invoices
                          (InvoiceDetail.tsx handles v1 invoices via isV1Invoice flag) */}
                      <TableCell>
                        <Stack direction="row" gap={1} alignItems="center">
                          <Link
                            to={`/dashboard/invoice-management/v2/${invoice.id}`}
                            style={{ color: TOKENS.primaryDefault }}
                          >
                            {invoice.invoice_no}
                          </Link>
                          <InfoTooltip title={rejectReasonFor(invoice)} />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>{invoice.vendor_name}</TableCell>
                      <TableCell>{convertDate(invoice.invoice_date)}</TableCell>
                      <TableCell>{convertToINR(invoice.invoice_value)}</TableCell>
                      <TableCell>
                        {convertToINR(invoice.amount_paid)}
                        {paymentEntry?.payment_date && (
                          <Typography sx={{ fontSize: 10 }}>
                            [{convertDate(paymentEntry.payment_date)}]
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{convertToINR(invoice.additional_charges)}</TableCell>
                      <TableCell>{invoice.irn_invoice ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {invoice.credit_notes?.[0] ? (
                          <MuiButton
                            disableRipple
                            onClick={() => setSelectedCreditNote(invoice.credit_notes[0])}
                            variant="text"
                            style={{
                              textDecoration: "underline",
                              color: "black",
                              padding: 0,
                              minWidth: 0,
                            }}
                          >
                            {invoice.credit_notes[0].number}
                          </MuiButton>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <UploadView
                          data={{ image: invoice.invoice_image }}
                          canUpload={!isUploadBlocked(invoice)}
                          onUploadClick={() => {
                            setSelectedUploadInvoiceId(invoice.id);
                            setUploadOpen(true);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <OrderStatusBadge type={statusCode} label={statusLabel} />
                      </TableCell>
                      {(canEdit || canDelete) && (
                        <TableCell>
                          <Stack direction="row">
                            {canEdit && EDITABLE_LIFECYCLE_STATUSES.has(invoice.lifecycle_status) && (
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/dashboard/invoice-management/v2/${invoice.id}/edit`)}
                              >
                                <EditOutlined fontSize="small" />
                              </IconButton>
                            )}
                            {canDelete && (
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(invoice)}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Card>

        {isInternal && !po && <GrnsSection grns={grnRows} />}

        {/* PO Items table — FINS POs only */}
        {po && (
          <Box>
            <Box display="flex" alignItems="center" gap="8px" mb={2}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px" }}>
                PO Items
              </Typography>
              <Typography sx={{ fontSize: 12, lineHeight: "16px", color: TOKENS.onSurfaceSecondary }}>
                ({items.length})
              </Typography>
            </Box>
            <Card sx={{ borderRadius: "8px", boxShadow: "none", overflow: "hidden" }}>
              {items.length === 0 ? (
                <Box sx={{ p: "24px", textAlign: "center" }}>
                  <Typography sx={{ fontSize: 14, color: TOKENS.onSurfaceSecondary }}>
                    No items on this PO.
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 560 }}>
                  <Table size="small" stickyHeader sx={{ minWidth: isMobile ? 1100 : undefined }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={ITEM_COL_HEADER_SX}>SO Item Name</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Total Amount with tax
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>Description</TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>Specification</TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>HSN/SAC Code (GST)</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Unit Cost Price
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          QTY
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>UOM</TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          Total Amount
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          CGST
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          SGST
                        </TableCell>
                        <TableCell align="right" sx={ITEM_COL_HEADER_SX}>
                          IGST
                        </TableCell>
                        <TableCell sx={ITEM_COL_HEADER_SX}>PO Remarks</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it, idx) => {
                        const tax = it.tax || {};
                        const amountWithTax =
                          it.amount != null ? it.amount + (tax.total || 0) : null;
                        const intra = tax.strategy === "intrastate";
                        const inter = tax.strategy === "interstate";
                        return (
                          <TableRow key={it.id || idx}>
                            <TableCell sx={{ fontSize: 14, lineHeight: "20px" }}>
                              {it.name || "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14, fontWeight: 500 }}>
                              {amountWithTax != null ? convertToINR(amountWithTax) : "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14, maxWidth: 240 }}>
                              {it.description || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14, maxWidth: 200 }}>
                              {it.specification || "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{tax.hsn || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {it.unit_cost_price != null ? convertToINR(it.unit_cost_price) : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {it.quantity ?? "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{it.uom || "-"}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {it.pre_tax_amount != null ? convertToINR(it.pre_tax_amount) : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {intra
                                ? `${convertToINR(tax.cgst_amount || 0)} (${tax.cgst != null ? tax.cgst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {intra
                                ? `${convertToINR(tax.sgst_amount || 0)} (${tax.sgst != null ? tax.sgst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: 14 }}>
                              {inter
                                ? `${convertToINR(tax.igst_amount || 0)} (${tax.igst != null ? tax.igst + "%" : "NA"})`
                                : "-"}
                            </TableCell>
                            <TableCell sx={{ fontSize: 14 }}>{it.remarks || "-"}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Box>
        )}
      </Box>

      {isMobile && (
        <WithPermission permission={BOUNCER_PERMISSIONS.INVOICE_UPLOAD}>
          <Fab
            color="primary"
            size="medium"
            aria-label="Upload Invoices"
            onClick={() => {
              setSelectedUploadInvoiceId(undefined);
              setUploadOpen(true);
            }}
            sx={MOBILE_FAB_SX}
          >
            <FileUpload />
          </Fab>
        </WithPermission>
      )}

      {selectedCreditNote && (
        <CreditNoteReview
          creditNote={selectedCreditNote}
          po_code={po_code}
          po_id={poData?.metadata?.po_id}
          handleClose={() => setSelectedCreditNote(null)}
        />
      )}

      {uploadModal}
    </Box>
  );
};

export default InvoiceDetailsV2;
